import { useCallback } from 'react';
import type Handsontable from 'handsontable';
import { beginRowDrag } from '../rowDragState';

/**
 * 首列拖拽图标根节点事件委托（hook 版）
 *
 * renderer 只画 DOM（见 createFirstColumnContent），完全不挂监听。
 * 根节点 capture 阶段一次性拦截：
 * - mousedown → beginRowDrag（点击选中 / 拖拽换行）
 * - contextmenu → 阻止冒泡，避免在 icon 上右键触发 HOT 的 beforeOnCellContextMenu
 *
 * 相比原先「每个 first-column td 各挂两个监听」，
 * 大表格下监听器数量从 O(rows) 降为 O(1)。
 *
 * @param onIconSelectedRow 拖拽 icon 单击/拖完时同步给外部的「当前选中行索引」
 * @returns install(hot)：在 afterInit 中调用，返回 teardown，afterDestroy 时调用即可
 */
export const useRowDragDelegation = (
  onIconSelectedRow: (rowIdx: number) => void,
): ((hot: Handsontable) => () => void) => {
  return useCallback((hot: Handsontable): (() => void) => {
    const root = hot.rootElement as HTMLElement;

    const onMouseDown = (e: MouseEvent) => {
      const btn = (e.target as HTMLElement | null)?.closest('.dba-smart-table-cell-action-icon');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const td = (btn as HTMLElement).closest('td') as HTMLElement | null;
      if (!td) return;
      let coords: Handsontable.CellCoords | null = null;
      try {
        coords = hot.getCoords(td) as Handsontable.CellCoords | null;
      } catch {
        coords = null;
      }
      if (!coords || coords.row < 0) return;
      beginRowDrag(hot, coords.row, e, onIconSelectedRow);
    };

    const onContextMenu = (e: MouseEvent) => {
      const btn = (e.target as HTMLElement | null)?.closest('.dba-smart-table-cell-action-icon');
      if (btn) e.stopPropagation();
    };

    root.addEventListener('mousedown', onMouseDown, true);
    root.addEventListener('contextmenu', onContextMenu, true);

    return () => {
      root.removeEventListener('mousedown', onMouseDown, true);
      root.removeEventListener('contextmenu', onContextMenu, true);
    };
  }, [onIconSelectedRow]);
};
